import { WarningIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { match } from 'ts-pattern'
import type { PolarisPushConfirmationModalProps } from '../../hooks/use-polaris-push-preflight.ts'
import {
  PolarisStatusStateSchema,
  SkippedEntryReasonSchema,
  type PolarisStatusResponse,
  type PolarisStatusState,
  type SkippedEntry,
} from '../../utils/api-client/api-schemas.ts'
import { getIsPolarisConfirmationRequired, getPolarisSkipReasonPhrase } from '../../utils/polaris-status.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import * as styles from './delete-scan-modal.css.ts'

/**
 * Counts skipped targets per reason, so each distinct reason gets its own line.
 *
 * Ordered by the schema's declared reasons rather than by the order the response happened to list
 * them, so the same set of skipped findings always reads the same way.
 */
const groupBySkipReason = (skipped: SkippedEntry[]) =>
  SkippedEntryReasonSchema.options
    .map(reason => [reason, skipped.filter(entry => entry.reason === reason).length] as const)
    .filter(([, count]) => count > 0)

/**
 * Describes one Polaris state to the user.
 *
 * Matched exhaustively over the state enum rather than re-listing the states this dialog knows about:
 * a state added to `getIsPolarisConfirmationRequired` reaches this dialog, and a compile error here
 * is what stops it arriving with no paragraph describing it while the push still includes it.
 */
const confirmationStateMessage = (state: PolarisStatusState, count: number) =>
  match(state)
    .with('conflict', () => (
      <>
        <strong>{count}</strong> {pluralize(count, 'classification has', 'classifications have')} a different status in
        Polaris. Pushing will overwrite the Polaris status.
      </>
    ))
    .with('not-found', () => (
      <>
        <strong>{count}</strong> {pluralize(count, 'classification was', 'classifications were')} not found in Polaris.
      </>
    ))
    .with('lookup-failed', () => (
      <>
        <strong>{count}</strong> {pluralize(count, 'classification could', 'classifications could')} not be checked
        because Polaris was unavailable. You can still push, but the current Polaris status is unknown.
      </>
    ))
    // Never reaches the dialog: the preflight only asks about states that need confirming.
    .with('match', 'not-applicable', () => undefined)
    .exhaustive()

/**
 * Counts the statuses the user is being asked about, per state.
 *
 * Ordered and filtered by the schema and the confirmation policy rather than by a hand-written list,
 * so the headline count and the paragraphs below it can never disagree about which states matter.
 */
const groupByConfirmationState = (statuses: PolarisStatusResponse[]) =>
  PolarisStatusStateSchema.options
    .filter(state => getIsPolarisConfirmationRequired(state))
    .map(state => [state, statuses.filter(status => status.state === state).length] as const)
    .filter(([, count]) => count > 0)

export const PolarisPushConfirmationModal: React.FC<PolarisPushConfirmationModalProps> = ({
  onCancel,
  onConfirm,
  isConfirming = false,
  ...confirmation
}) => (
  <Dialog.Root
    open={true}
    onOpenChange={open => {
      // Not dismissible while the confirmed push is in flight: it would report the push as declined
      // while it completes anyway.
      if (!open && !isConfirming) {
        onCancel()
      }
    }}
  >
    <Dialog.Portal>
      <Dialog.Overlay className={styles.alertDialogOverlay} />
      <Dialog.Content className={styles.alertDialogContent}>
        <Dialog.Title className={styles.alertDialogTitle}>
          <WarningIcon className={styles.alertDialogCautionIcon} size={32} weight="fill" />
          Review Polaris status
        </Dialog.Title>
        <Dialog.Description className={styles.alertDialogDescription} asChild>
          <div>
            {/* Either Polaris answered or it did not: the per-status breakdown and the "could not be
                reached" paragraph describe the same targets in incompatible ways, so the union decides
                which of them the user reads. */}
            {match(confirmation)
              .with({ state: 'lookup-failed' }, ({ reason }) => (
                <p className={styles.alertDialogDescriptionParagraph}>
                  Polaris could not be reached ({reason}), so the current status of these classifications is unknown.
                  You can still push, but anything already triaged in Polaris will be overwritten.
                </p>
              ))
              .with({ state: 'reviewed' }, ({ statuses, skipped }) => {
                const statusGroups = groupByConfirmationState(statuses)
                const affectedCount = statusGroups.reduce((total, [, count]) => total + count, 0) + skipped.length
                return (
                  <>
                    <p className={styles.alertDialogDescriptionParagraph}>
                      {affectedCount} {pluralize(affectedCount, 'classification', 'classifications')} could not be
                      confirmed as matching Polaris.
                    </p>
                    {statusGroups.map(([state, count]) => (
                      <p className={styles.alertDialogDescriptionParagraph} key={state}>
                        {confirmationStateMessage(state, count)}
                      </p>
                    ))}
                    {groupBySkipReason(skipped).map(([reason, count]) => (
                      <p className={styles.alertDialogDescriptionParagraph} key={reason}>
                        <strong>{count}</strong> {pluralize(count, 'classification was', 'classifications were')} not
                        checked because {getPolarisSkipReasonPhrase(reason)}.
                      </p>
                    ))}
                  </>
                )
              })
              .exhaustive()}
            <p className={styles.alertDialogDescriptionParagraph}>Do you want to continue?</p>
          </div>
        </Dialog.Description>
        <div className={styles.alertDialogButtons}>
          <GhostButton type="secondary" onClick={onCancel} state={isConfirming ? 'disabled' : 'default'}>
            Cancel
          </GhostButton>
          <DefaultButton buttonType="secondary" onClick={onConfirm} state={isConfirming ? 'loading' : 'default'}>
            Push anyway
          </DefaultButton>
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
)

/**
 * Renders the confirmation dialog only while a push is actually waiting on the user.
 *
 * Takes `usePolarisPushPreflight`'s `confirmationDialogProps` as-is, so the rule that ties "there is a
 * pending confirmation" to "the dialog is open" lives here rather than being restated at each surface
 * that pushes.
 */
export const PolarisPushConfirmationPrompt: React.FC<{ dialogProps?: PolarisPushConfirmationModalProps }> = ({
  dialogProps,
}) => (dialogProps ? <PolarisPushConfirmationModal {...dialogProps} /> : undefined)
